import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Wind, WashingMachine, Paintbrush, DoorOpen, Droplets, SprayCan, Wrench, ArrowUpRight } from "lucide-react";
import { useLang, CATEGORIES } from "../i18n";

const iconMap = { Wind, WashingMachine, Paintbrush, DoorOpen, Droplets, SprayCan, Wrench };

export default function Services() {
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <section id="services" className="py-24 md:py-40 bg-crema" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="max-w-3xl mb-14 md:mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display font-black tracking-tight text-charcoal text-4xl md:text-5xl"
            data-testid="services-title"
          >
            {t.services.title}
          </motion.h2>
          <p className="mt-4 text-lg text-warmgray">{t.services.subtitle}</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {CATEGORIES.map((cat, i) => {
            const Icon = iconMap[cat.icon] || Wrench;
            const info = t.services.items[cat.id];
            const featured = cat.id === "general";
            return (
              <motion.button
                key={cat.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                onClick={() => navigate(`/book?category=${cat.id}`)}
                className={`group relative text-left rounded-2xl overflow-hidden border transition-all duration-500 ${
                  featured
                    ? "sm:col-span-2 lg:col-span-1 bg-charcoal border-charcoal"
                    : "bg-white border-charcoal/10 hover:border-teal/40"
                } hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(43,42,40,0.35)]`}
                data-testid={`service-card-${cat.id}`}
              >
                {!featured && (
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={cat.image}
                      alt={info.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                    {info.price && (
                      <div
                        className="absolute top-3 left-3 inline-flex items-baseline gap-1 rounded-full bg-crema/95 backdrop-blur-sm pl-3 pr-3.5 py-1.5 shadow-md ring-1 ring-charcoal/5"
                        data-testid={`service-price-${cat.id}`}
                      >
                        <span className="text-[11px] font-medium text-warmgray">{t.services.from}</span>
                        <span className="text-sm font-bold text-teal">{info.price}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className={`p-6 ${featured ? "py-10" : ""}`}>
                  <div className="flex items-center justify-between mb-3">
                    <Icon
                      className={`h-6 w-6 ${featured ? "text-teal" : "text-teal"}`}
                      strokeWidth={1.8}
                    />
                    <ArrowUpRight
                      className={`h-5 w-5 transition-all duration-300 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 ${
                        featured ? "text-crema" : "text-coral"
                      }`}
                    />
                  </div>
                  {featured && info.quote && (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full bg-teal/15 text-teal px-3 py-1 mb-4 text-xs font-bold"
                      data-testid={`service-price-${cat.id}`}
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-teal" />
                      {info.quote}
                    </span>
                  )}
                  <h3
                    className={`font-display font-bold text-xl mb-2 ${
                      featured ? "text-crema" : "text-charcoal"
                    }`}
                  >
                    {info.name}
                  </h3>
                  <p className={`text-sm leading-relaxed ${featured ? "text-crema/60" : "text-warmgray"}`}>
                    {info.line}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
